/**
 * `<PanelHost>` — the MODAL presenter.
 *
 * Wraps a subtree, provides `usePanel()`, and renders at most one panel at a
 * time in a portal over the rest of the UI. Everything here is a presentation
 * concern — portal, scrim, queueing, dialog semantics, focus management — which
 * is exactly why it is separate from `<Panel>`: inline panels get none of it.
 *
 * `showPanel()` returns a promise that resolves with the outcome, so calling
 * code reads as a straight line:
 *
 * ```tsx
 * const outcome = await showPanel(definition, { context: { items } });
 * if (outcome.type === 'emit' && outcome.token === 'next_level') …
 * ```
 *
 * The host never performs a navigation itself. `open_arche` / `open_url`
 * outcomes are handed back to the caller, which executes them through the path
 * that already gates that privilege.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { parsePanelAction } from './action';
import { Panel } from './Panel';
import { resolvePanelPresentation } from './presentation';
import type {
  PanelContent,
  PanelDefinition,
  PanelOutcome,
  PanelPresentation,
  PanelRenderContext,
} from './types';

export interface ShowPanelOptions {
  /** Runtime values, items and image resolution. */
  context?: PanelRenderContext;
  /**
   * Overrides the definition's authored `presentation`. The definition
   * suggests; the call site decides.
   */
  presentation?: PanelPresentation;
}

export interface PanelHostApi {
  showPanel: (
    definition: PanelDefinition | PanelContent,
    options?: ShowPanelOptions,
  ) => Promise<PanelOutcome>;
}

interface QueueEntry {
  key: number;
  definition: PanelContent;
  presentation: Required<PanelPresentation>;
  context: PanelRenderContext | undefined;
  resolve: (outcome: PanelOutcome) => void;
}

const PanelHostContext = createContext<PanelHostApi | null>(null);

/**
 * Access the modal presenter. Throws when used outside a `<PanelHost>` — a
 * silent no-op would look like "the panel just never appeared", which is much
 * harder to diagnose than a thrown error at the call site.
 */
export function usePanel(): PanelHostApi {
  const api = useContext(PanelHostContext);
  if (!api) {
    throw new Error('usePanel() must be used inside a <PanelHost>. Wrap your app or arche in one.');
  }
  return api;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function PanelHost({ children }: { children?: ReactNode }): ReactNode {
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const nextKey = useRef(0);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Mirrors `queue` for reads from callbacks that must not put side effects in a
  // state updater (see `settle`) and for the unmount sweep below.
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const showPanel = useCallback<PanelHostApi['showPanel']>((definition, options) => {
    return new Promise<PanelOutcome>((resolve) => {
      const presentation = resolvePanelPresentation(
        (definition as PanelDefinition).presentation,
        options?.presentation,
      );
      setQueue((current) => {
        const entry: QueueEntry = {
          key: nextKey.current++,
          definition,
          presentation,
          context: options?.context,
          resolve,
        };
        if (current.length === 0) return [entry];
        // The panel already on screen is NEVER preempted — pulling a dialog out
        // from under someone mid-read is worse than making a high-priority panel
        // wait its turn. Priority orders the PENDING tail only; ties keep
        // insertion order (stable sort).
        const [showing, ...pending] = current;
        return [
          showing,
          ...[...pending, entry].sort((a, b) => b.presentation.priority - a.presentation.priority),
        ];
      });
    });
  }, []);

  const active = queue[0];

  /**
   * Pop the showing panel and resolve its promise.
   *
   * The `resolve` call is deliberately OUTSIDE the state updater. A React state
   * updater must be pure: StrictMode double-invokes it in development, and React
   * may replay it. Resolving a promise happens to be idempotent, so the previous
   * version worked — but it was a trap for the next person to add a side effect
   * (telemetry, an audit hook) alongside it. The `current[0] === head` guard
   * keeps the updater itself idempotent under a double invocation.
   */
  const settle = useCallback((outcome: PanelOutcome) => {
    const head = queueRef.current[0];
    if (!head) return;
    setQueue((current) => (current[0] === head ? current.slice(1) : current));
    head.resolve(outcome);
  }, []);

  /**
   * `dismissible: false` removes the explicit close affordance, but a panel the
   * user cannot leave — no close, Escape refused, focus trapped — must never
   * exist. So the authored hint loses to the escape hatch whenever the panel has
   * no way out of its own.
   *
   * "A way out" means an ACTIONABLE button, not merely a button element: the
   * renderer disables any button whose action fails `parsePanelAction`, so a
   * panel whose only buttons are inert (an in-code author's typo — such
   * definitions never pass the server-side write gate that would have rejected
   * the action) provides no exit. Counting those buttons as escape routes was
   * exactly the hole that trapped the user.
   */
  const hasActionableButton =
    active?.definition.elements.some(
      (element) => element.kind === 'button' && parsePanelAction(element.action) !== null,
    ) ?? false;
  const dismissible = active ? active.presentation.dismissible || !hasActionableButton : false;

  const dismiss = useCallback(() => {
    if (!dismissible) return;
    settle({ type: 'dismissed' });
  }, [dismissible, settle]);

  // Dialog semantics: focus the panel on open, keep Tab inside it, close on
  // Escape, and restore focus to whatever was focused before. None of this
  // exists for inline panels — see the note at the top of Panel.tsx.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!active || !dialog) return;

    const doc = dialog.ownerDocument;
    const previouslyFocused = doc.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    // Focus the DIALOG itself, not its first control. Focusing the first
    // control means focusing the close "×" (it comes first in DOM order so it
    // can be positioned in the corner), which is both a poor first stop and a
    // one-keystroke path to accidentally dismissing the panel. Focusing the
    // container announces the panel's name and content, and the first Tab then
    // moves into the controls in reading order.
    dialog.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only claim Escape when we actually act on it. The previous version
        // called preventDefault() before checking, so a NON-dismissible panel
        // half-swallowed the key: it suppressed the default without closing
        // anything, leaving the app's other Escape handling in an ambiguous
        // state. A modal that handles Escape owns it completely (stopPropagation
        // too, so nothing behind the scrim also reacts); one that does not
        // handle it leaves the event entirely alone.
        if (!dismissible) return;
        event.preventDefault();
        event.stopPropagation();
        dismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const currentIndex = items.indexOf(doc.activeElement as HTMLElement);
      const lastIndex = items.length - 1;
      if (event.shiftKey && currentIndex <= 0) {
        event.preventDefault();
        items[lastIndex].focus();
      } else if (!event.shiftKey && currentIndex === lastIndex) {
        event.preventDefault();
        items[0].focus();
      }
    };

    doc.addEventListener('keydown', onKeyDown, true);
    return () => {
      doc.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [active, dismiss, dismissible]);

  /**
   * Lock background scroll while any panel is showing.
   *
   * The scrim covers the viewport, but with a short panel a wheel/touch scroll
   * chains through to the document behind it — the page visibly moves under a
   * modal that is supposed to be blocking. Keyed on whether a panel is showing at
   * all (not on which one), so a queue draining from one panel to the next does
   * not flicker the scrollbar.
   *
   * Background content is otherwise made unavailable by `aria-modal="true"` plus
   * the focus trap below, rather than by mutating `inert`/`aria-hidden` on the
   * host app's tree — a shared primitive should not be reaching outside its own
   * portal to rewrite attributes it does not own.
   */
  const hasActivePanel = active != null;
  useEffect(() => {
    if (!hasActivePanel) return;
    const body = dialogRef.current?.ownerDocument?.body ?? document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [hasActivePanel]);

  // Every queued panel must settle if the host unmounts mid-flight, or an
  // `await showPanel(...)` would hang forever.
  useEffect(() => {
    return () => {
      for (const entry of queueRef.current) entry.resolve({ type: 'dismissed' });
    };
  }, []);

  const api = useMemo<PanelHostApi>(() => ({ showPanel }), [showPanel]);

  return (
    <PanelHostContext.Provider value={api}>
      {children}
      {active && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fias-panel-scrim"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) dismiss();
              }}
            >
              <Panel
                ref={dialogRef}
                definition={active.definition}
                context={active.context}
                size={active.presentation.size}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                onAction={(action) => settle(action)}
                onDismiss={dismissible ? dismiss : undefined}
              />
            </div>,
            document.body,
            String(active.key),
          )
        : null}
    </PanelHostContext.Provider>
  );
}
